import {
  Alert,
  ExternalLink,
  fontStyles,
  InlineExternalLink,
} from '@monash/portal-react';

import Card from '../../ui/card/Card';
import { Table, TableCell, TableRow } from './table/Table';
import { convertToYesNo } from './utils/convert-to-yes-no';
import { getMinutesPerHour } from './utils/get-minutes-per-hour';
import { useContext } from 'react';
import { StudentContext } from '../../providers/StudentProvider';
import c from './student-services.module.scss';
import { toSentenceCase } from './utils/to-sentence-case';
import PageContainer from '../../ui/page-container/PageContainer';

const StudentServices = () => {
  const { profileData } = useContext(StudentContext);
  const { disabilitySupportDetails } = profileData || {};
  const hasError = disabilitySupportDetails?.success === false;

  const {
    auslanInterpreter,
    extraWritingTime,
    extraWritingTimeDescription,
    facultyNotes,
    flexibleAttendance,
    privateRoom,
    reader,
    requires,
    restMovement,
    restMovementDescription,
    scribe,
    threeSidedCubicle,
    documentationExpirySemester,
    documentationExpiryYear,
    parsedDisabilityAdviser,
    specialConsideration,
    preferentialTimetabling,
  } = disabilitySupportDetails || {};

  const hasDisabilityServices = Boolean(disabilitySupportDetails);

  return (
    <PageContainer>
      <div className={c.wrapper}>
        <section className={c.rows} aria-label="Student services">
          <h1 className={fontStyles.heading} tabIndex={-1}>
            Student services
          </h1>

          <Card>
            <div className={c.rows}>
              <p>
                From academic support to student life, we've got you covered!
                Explore everything available to help you thrive at Monash.
              </p>

              <ExternalLink
                text="View all services"
                link="https://www.monash.edu/students/support/finder"
                variant="primary"
                mode="card"
                className={c.viewServicesButton}
              />
            </div>
          </Card>
        </section>

        <section className={c.rows} aria-label="Disability student services">
          <h2 className={fontStyles.heading}>Disability student services</h2>

          <Card>
            <span>
              If you'd like to register for disability services, or you have
              concerns about your current arrangements, visit{' '}
              <InlineExternalLink
                href="https://www.monash.edu/students/support/disability"
                text="Disability Support Services"
              />{' '}
              for more information.
            </span>
          </Card>

          {hasDisabilityServices && !hasError && (
            <>
              <Card>
                <section aria-label="Alternative arrangements">
                  <h3 className={`${fontStyles.heading} ${fontStyles.md}`}>
                    Alternative arrangements
                  </h3>

                  <h4 className={c.tableHeading}>
                    Class tests, eExams and department-run exams
                  </h4>

                  <Table>
                    {restMovement && (
                      <TableRow>
                        <TableCell>Rest & movement</TableCell>
                        <TableCell>
                          {convertToYesNo(restMovement)}
                          <br />
                          {getMinutesPerHour(restMovementDescription)}
                        </TableCell>
                      </TableRow>
                    )}

                    <TableRow>
                      <TableCell>Extra writing time</TableCell>
                      <TableCell>
                        {convertToYesNo(extraWritingTime)}
                        <br />
                        {getMinutesPerHour(extraWritingTimeDescription)}
                      </TableCell>
                    </TableRow>

                    {scribe && (
                      <TableRow>
                        <TableCell>Scribe</TableCell>
                        <TableCell>{convertToYesNo(scribe)}</TableCell>
                      </TableRow>
                    )}

                    {reader && (
                      <TableRow>
                        <TableCell>Reader</TableCell>
                        <TableCell>{convertToYesNo(reader)}</TableCell>
                      </TableRow>
                    )}

                    {auslanInterpreter && (
                      <TableRow>
                        <TableCell>Auslan Interpreter</TableCell>
                        <TableCell>
                          {convertToYesNo(auslanInterpreter)}
                        </TableCell>
                      </TableRow>
                    )}

                    {requires && (
                      <TableRow>
                        <TableCell>Requires</TableCell>
                        <TableCell>{toSentenceCase(requires)}</TableCell>
                      </TableRow>
                    )}
                  </Table>

                  {(privateRoom || threeSidedCubicle) && (
                    <>
                      <h3 className={c.tableHeading}>On-campus Exams</h3>
                      <Table>
                        {privateRoom && (
                          <TableRow>
                            <TableCell>Private room</TableCell>
                            <TableCell>{convertToYesNo(privateRoom)}</TableCell>
                          </TableRow>
                        )}

                        {threeSidedCubicle && (
                          <TableRow>
                            <TableCell>Three-sided cubicle</TableCell>
                            <TableCell>
                              {convertToYesNo(threeSidedCubicle)}
                            </TableCell>
                          </TableRow>
                        )}
                      </Table>
                    </>
                  )}

                  {facultyNotes && (
                    <>
                      <h3 className={c.tableHeading}>Faculty notes</h3>

                      <Table>
                        <TableRow>
                          <TableCell>Notes</TableCell>
                          <TableCell>{toSentenceCase(facultyNotes)}</TableCell>
                        </TableRow>
                      </Table>
                    </>
                  )}
                </section>
              </Card>

              {documentationExpirySemester && documentationExpiryYear && (
                <Card>
                  <section aria-label="Documentation expiry">
                    <h2 className={`${fontStyles.heading} ${fontStyles.md}`}>
                      Documentation expiry
                    </h2>
                    <p className={c.subtext}>Your documentation expires:</p>
                    <Table>
                      <TableRow>
                        <TableCell>Semester</TableCell>
                        <TableCell>{documentationExpirySemester}</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>Year</TableCell>
                        <TableCell>{documentationExpiryYear}</TableCell>
                      </TableRow>
                    </Table>
                  </section>
                </Card>
              )}

              {parsedDisabilityAdviser && (
                <Card>
                  <section aria-label="Disability advisor">
                    <h3 className={`${fontStyles.heading} ${fontStyles.md}`}>
                      Disability advisor
                    </h3>

                    <Table>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>
                          {parsedDisabilityAdviser?.fullName}
                        </TableCell>
                      </TableRow>
                    </Table>
                  </section>
                </Card>
              )}

              {(specialConsideration ||
                preferentialTimetabling ||
                flexibleAttendance) && (
                <Card>
                  <section aria-label="Support checklist">
                    <h3 className={`${fontStyles.heading} ${fontStyles.md}`}>
                      Support checklist
                    </h3>

                    <Table>
                      {specialConsideration && (
                        <TableRow>
                          <TableCell>Special consideration</TableCell>
                          <TableCell>
                            {convertToYesNo(specialConsideration)}
                          </TableCell>
                        </TableRow>
                      )}

                      {preferentialTimetabling && (
                        <TableRow>
                          <TableCell>Preferential timetabling</TableCell>
                          <TableCell>
                            {convertToYesNo(preferentialTimetabling)}
                          </TableCell>
                        </TableRow>
                      )}

                      {flexibleAttendance && (
                        <TableRow>
                          <TableCell>Flexible attendance</TableCell>
                          <TableCell>
                            {convertToYesNo(flexibleAttendance)}
                          </TableCell>
                        </TableRow>
                      )}
                    </Table>
                  </section>
                </Card>
              )}
            </>
          )}
        </section>

        {hasError && (
          <section>
            <Card>
              <Alert type="error">
                Sorry, we're having trouble retrieving your disability
                information right now. Please try again later.
              </Alert>
            </Card>
          </section>
        )}
      </div>
    </PageContainer>
  );
};

export default StudentServices;
